import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { endpoints } from "../endpoints";
import { getResource } from "../https";
import { Meta, Product } from "../types";
import { querykeys } from "../querykeys";

type ProductListResponse = {
  success: boolean;
  message: string;
  data: {
    products: Product[];
    pagination: Meta;
  };
};

type FeaturedResponse = {
  data: Product[];
};

type ProductByIdResponse = {
    data: Product;
  };

type ProductListPageData = {
  data: ProductListResponse;
  nextPage: number;
};

type ProductFilters = {
  search?: string;
  categoryId?: string;
  subcategoryId?: string;
  subsubcategoryId?: string;
  brandId?: string;
  minPrice?: number;
  maxPrice?: number;
  minRating?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
};

export const getAllProducts = async (
  pageNo: number,
  pageSize: number,
  filters?: ProductFilters
): Promise<ProductListResponse> => {
  // Build query parameters
  const params = new URLSearchParams({
    page: pageNo.toString(),
    limit: pageSize.toString(),
  });

  // Add optional filters if they exist
  if (filters) {
    if (filters.search) params.append("search", filters.search);
    if (filters.categoryId) params.append("categoryId", filters.categoryId);
    if (filters.subcategoryId) params.append("subcategoryId", filters.subcategoryId);
    if (filters.subsubcategoryId) params.append("subsubcategoryId", filters.subsubcategoryId);
    if (filters.brandId) params.append("brandId", filters.brandId);
    if (filters.minPrice !== undefined)
      params.append("minPrice", filters.minPrice.toString());
    if (filters.maxPrice !== undefined)
      params.append("maxPrice", filters.maxPrice.toString());
    if (filters.minRating !== undefined)
      params.append("minRating", filters.minRating.toString());
    if (filters.sortBy) params.append("sortBy", filters.sortBy);
    if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);
  }

  return await getResource({
    pathUrl: `${endpoints.products}?${params.toString()}`,
  });
};

export const getFeaturedProducts = async (): Promise<FeaturedResponse> => {
  return await getResource({
    pathUrl: `${endpoints.products}/featured?limit=${10}`,
  });
};

export const getRelatedProducts = async (id: number): Promise<FeaturedResponse> => {
    return await getResource({
      pathUrl: `${endpoints.products}/${id}?limit=${15}`,
    });
};

export const getProductById = async (id: number): Promise<ProductByIdResponse> => {
    return await getResource({
      pathUrl: `${endpoints.products}/${id}`,
    });
};


export const useGetAllProducts = (filters?: ProductFilters) => {
  const pageSize = 20;

  return useInfiniteQuery({
    queryKey: [querykeys.GET_ALL_PRODUCTS, filters],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await getAllProducts(pageParam, pageSize, filters);
      return {
        data: response,
        nextPage: pageParam + 1,
      } as ProductListPageData;
    },
    getNextPageParam: (lastPage: ProductListPageData) => {
      const metadata = lastPage?.data?.data?.pagination;
      return metadata?.hasNext ? lastPage?.nextPage : undefined;
    },
    initialPageParam: 1,
  });
};

export const useGetFeaturedProducts = () => {
  const query = useQuery({
    queryKey: [querykeys.GET_FEATURED_PRODUCTS],
    queryFn: () => getFeaturedProducts(),
  });
  return query;
};

export const useGetRelatedProducts = (id: number) => {
    const query = useQuery({
      queryKey: [querykeys.GET_RELATED_PRODUCTS],
      queryFn: () => getRelatedProducts(id),
    });
    return query;
};
  
export const useGetProductById = (id: number) => {
    const query = useQuery({
      queryKey: [querykeys.GET_SINGLE_PRODUCT, id],
      queryFn: () => getProductById(id),
    });
    return query;
};
  